import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, Award, Clock, Home as HomeIcon, FileCheck, Users, Star } from "lucide-react";
import { Divider } from "@/components/Divider";
import { SectionHeader } from "@/components/SectionHeader";
import { api } from "@/lib/api";
import { useContent } from "@/hooks/useContent";

const programs = [
  { id: "conventional", name: "Conventional", blurb: "Flexible terms for qualified buyers, 5% down available." },
  { id: "fha", name: "FHA", blurb: "Low 3.5% down. Friendly credit guidelines for first-time buyers." },
  { id: "va", name: "VA", blurb: "Zero down, no PMI, exclusive benefits for veterans & service members." },
  { id: "usda", name: "USDA", blurb: "Rural homebuyer program with 0% down for eligible areas." },
  { id: "jumbo", name: "Jumbo", blurb: "Financing above conforming limits for higher-value homes." },
  { id: "refinance", name: "Refinance", blurb: "Lower payments, take cash out, or shorten your term." },
];

export default function Home() {
  const [rates, setRates] = useState(null);
  const { t } = useContent();

  useEffect(() => {
    api.get("/rates").then((r) => setRates(r.data)).catch(() => {});
  }, []);

  const headlineL2Raw = t("home.headline_l2", "The mortgage you deserve.");
  const highlight = t("home.headline_highlight", "deserve");
  // Split second headline around the highlighted word for inline color treatment
  const splitParts = headlineL2Raw.split(new RegExp(`(${highlight})`, "i"));

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section className="relative bg-white overflow-hidden">
        <div className="absolute inset-0 hero-grid opacity-60" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 animate-fade-up">
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#00A651] mb-5">
              {t("home.eyebrow", "Mortgage Originator · NMLS #000000")}
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-black">
              {t("home.headline_l1", "The home you want.")}
              <br />
              {splitParts.map((part, i) =>
                part.toLowerCase() === highlight.toLowerCase()
                  ? <span key={i} className="text-[#00A651]">{part}</span>
                  : <React.Fragment key={i}>{part}</React.Fragment>
              )}
            </h1>
            <p className="font-sub italic text-lg sm:text-xl text-gray-700 mt-5 max-w-2xl">
              {t("home.subhead", "Insuring Your Future…Today — purchase, refinance, and investment financing with a personal originator who answers your call.")}
            </p>
            <Divider className="my-6" />
            <div className="flex flex-wrap gap-4">
              <Link
                to="/apply"
                data-testid="hero-apply-btn"
                className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#008f45] text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
              >
                {t("home.cta_primary", "Get Pre-Approved")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/calculator"
                data-testid="hero-calculator-btn"
                className="inline-flex items-center gap-2 bg-white border-2 border-[#F26522] hover:bg-[#F26522] hover:text-white text-[#F26522] font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
              >
                {t("home.cta_secondary", "Estimate Payment")}
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-xl">
              {[
                { n: t("home.stat1_n", "20+"), l: t("home.stat1_l", "Years Combined"), c: "#00A651" },
                { n: t("home.stat2_n", "1,500+"), l: t("home.stat2_l", "Families Funded"), c: "#E88C2F" },
                { n: t("home.stat3_n", "4.9★"), l: t("home.stat3_l", "Client Rating"), c: "#ED1C24" },
              ].map((s) => (
                <div key={s.l} className="border-l-2 pl-4" style={{ borderColor: s.c }}>
                  <div className="font-heading font-bold text-2xl text-black">{s.n}</div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5 relative animate-fade-up delay-2">
            <div className="relative">
              <img
                src="https://images.pexels.com/photos/7642218/pexels-photo-7642218.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=820&w=940"
                alt="Family in front of their new home"
                className="w-full h-[460px] object-cover rounded-sm shadow-xl"
                data-testid="hero-image"
              />
              <div className="absolute -bottom-6 -left-6 hidden sm:block bg-white border border-gray-200 shadow-lg p-5 max-w-[260px] rounded-sm">
                <div className="flex items-center gap-2 text-[#00A651] mb-2">
                  <ShieldCheck size={18} />
                  <span className="text-xs uppercase font-bold tracking-wider">Locked Rate</span>
                </div>
                <div className="font-heading font-bold text-3xl text-black">6.625%</div>
                <div className="text-xs text-gray-500 mt-1">30-Yr Fixed · 6.781% APR</div>
              </div>
              <div className="absolute -top-5 -right-5 hidden sm:block bg-black text-white p-4 max-w-[180px] rounded-sm">
                <Award size={18} className="text-[#E88C2F] mb-1" />
                <p className="text-xs uppercase tracking-wider font-bold">
                  Equal Housing Lender
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="brand-divider-full" />

      {/* TRUST BAR */}
      <section className="bg-[#F8FAFC] py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { Icon: Clock, t: "Quick Pre-Approval", s: "Decision in 24 hours", c: "#00A651" },
            { Icon: ShieldCheck, t: "Licensed & Insured", s: "Fully NMLS compliant", c: "#E88C2F" },
            { Icon: Users, t: "Personal Originator", s: "One contact, start to close", c: "#ED1C24" },
            { Icon: FileCheck, t: "Transparent Pricing", s: "No surprises at closing", c: "#F26522" },
          ].map(({ Icon, t, s, c }) => (
            <div key={t} className="flex items-start gap-4">
              <div
                className="w-11 h-11 grid place-items-center rounded-sm"
                style={{ backgroundColor: `${c}1A`, color: c }}
              >
                <Icon size={20} />
              </div>
              <div>
                <p className="font-heading font-bold text-sm text-black">{t}</p>
                <p className="text-xs text-gray-500 mt-0.5">{s}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Loan Programs"
            title="Financing built around you"
            subtitle="Six programs. One originator. Every closing handled personally."
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {programs.map((p, i) => {
              const accents = ["#00A651", "#E88C2F", "#ED1C24", "#F26522", "#00A651", "#E88C2F"];
              const c = accents[i % accents.length];
              return (
                <Link
                  key={p.id}
                  to={`/loan-programs#${p.id}`}
                  data-testid={`program-card-${p.id}`}
                  className="group block bg-white border border-gray-200 hover:shadow-md transition-all p-7 rounded-sm border-t-4"
                  style={{ borderTopColor: c }}
                >
                  <div className="flex items-start justify-between">
                    <h3 className="font-heading font-bold text-xl text-black">{p.name}</h3>
                    <ArrowRight
                      size={18}
                      className="text-gray-400 group-hover:text-[#F26522] group-hover:translate-x-1 transition-all"
                    />
                  </div>
                  <p className="font-sub italic text-gray-600 mt-2 text-sm">{p.blurb}</p>
                  <p className="mt-5 text-xs uppercase tracking-wider font-bold" style={{ color: c }}>
                    Learn more →
                  </p>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* RATES STRIP */}
      {rates && (
        <section className="bg-black text-white py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-8">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#E88C2F] mb-3 inline-flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-[#00A651] animate-pulse" />
                  Live Rates · Updated {new Date(rates.as_of).toLocaleTimeString()}
                </p>
                <h2 className="font-heading font-bold text-3xl sm:text-4xl">
                  Real rates. Real time.
                </h2>
              </div>
              <p className="font-sub italic text-gray-400 max-w-md">
                Rates refresh every {rates.bucket_minutes} minutes. Your actual rate depends on credit, LTV,
                property type, and program.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="rates-grid">
              {rates.rates.map((r) => {
                const up = r.direction === "up";
                const flat = r.direction === "flat";
                const color = flat ? "#9CA3AF" : (up ? "#00A651" : "#ED1C24");
                return (
                  <div
                    key={r.program}
                    className="border border-gray-800 hover:border-[#00A651] p-6 transition-colors rounded-sm"
                    data-testid={`rate-row-${r.program.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="text-xs uppercase tracking-widest text-gray-400">{r.program}</div>
                      <span
                        className="text-xs font-bold flex items-center gap-1"
                        style={{ color }}
                      >
                        {flat ? "·" : (up ? "▲" : "▼")}
                        {r.change_bp > 0 ? "+" : ""}{r.change_bp}bp
                      </span>
                    </div>
                    <div className="font-heading font-bold text-4xl mt-2">{r.rate.toFixed(3)}%</div>
                    <div className="text-xs text-gray-500 mt-1">{r.apr.toFixed(3)}% APR</div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* PROCESS */}
      <section className="py-20 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="The Process"
            title="From application to keys in hand"
            subtitle="A simple, transparent five-step path to closing."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {[
              { n: "01", t: "Apply Online", s: "Complete a secure application in 10 minutes.", c: "#00A651" },
              { n: "02", t: "Pre-Approval", s: "We verify income, credit, and assets fast.", c: "#E88C2F" },
              { n: "03", t: "Find Your Home", s: "Shop with confidence and a real budget.", c: "#ED1C24" },
              { n: "04", t: "Underwriting", s: "We coordinate everything behind the scenes.", c: "#F26522" },
              { n: "05", t: "Close & Move In", s: "Sign, fund, and celebrate.", c: "#000000" },
            ].map((step, i) => (
              <div key={step.n} className="bg-white border border-gray-200 p-6 rounded-sm relative border-b-4" style={{ borderBottomColor: step.c }}>
                <div className="font-heading font-bold text-3xl" style={{ color: step.c }}>{step.n}</div>
                <h3 className="font-heading font-bold text-base text-black mt-3">{step.t}</h3>
                <p className="text-sm text-gray-600 mt-2">{step.s}</p>
                {i < 4 && (
                  <ArrowRight
                    size={18}
                    className="hidden lg:block absolute -right-3 top-1/2 -translate-y-1/2 text-[#F26522] bg-[#F8FAFC] px-0.5"
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeader
            eyebrow="Client Voices"
            title="People who trusted us with their biggest decision"
          />
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                q: "Weithers walked us through every line of the closing disclosure. We felt informed, not rushed.",
                n: "M. & J. Adams",
                r: "First-Time Buyers · FHA",
                c: "#00A651",
              },
              {
                q: "We refinanced and cut our payment by $312/month. The break-even math made sense from day one.",
                n: "Priya R.",
                r: "Refinance Client",
                c: "#E88C2F",
              },
              {
                q: "As a veteran, I appreciated how seriously they took the VA paperwork. Smooth start to finish.",
                n: "SSgt David K.",
                r: "VA Purchase",
                c: "#ED1C24",
              },
            ].map((t) => (
              <figure
                key={t.n}
                className="bg-white border border-gray-200 p-7 rounded-sm border-l-4"
                style={{ borderLeftColor: t.c }}
              >
                <div className="flex gap-1" style={{ color: t.c }}>
                  {[...Array(5)].map((_, i) => <Star key={i} size={14} fill={t.c} />)}
                </div>
                <blockquote className="font-sub italic text-gray-700 mt-4 leading-relaxed">
                  “{t.q}”
                </blockquote>
                <figcaption className="mt-5">
                  <div className="font-heading font-bold text-sm text-black">{t.n}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider">{t.r}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#00A651] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] font-bold opacity-90 mb-3">
              Ready When You Are
            </p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight">
              Start your mortgage in 10 minutes.
            </h2>
            <p className="font-sub italic text-lg mt-3 opacity-90">
              No commitment. No hard credit pull on pre-qualification.
            </p>
          </div>
          <div className="flex flex-wrap gap-4">
            <Link
              to="/apply"
              data-testid="footer-cta-apply"
              className="bg-white text-[#00A651] hover:bg-black hover:text-white font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
            >
              Apply Now
            </Link>
            <Link
              to="/contact"
              data-testid="footer-cta-contact"
              className="border-2 border-white text-white hover:bg-white hover:text-[#00A651] font-bold uppercase tracking-wider text-sm px-7 py-4 rounded-sm transition-colors"
            >
              Talk to a Human
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
